import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { getOrCreateAnonUserId, getAnonUserId, setFirebaseUid, clearFirebaseUid, getFirebaseUid, setFirebaseEmail, clearFirebaseEmail, getFirebaseEmail } from '../utils/user';
import { env } from '@/config/env';

interface AuthContextType {
  user: User | null;
  guestUserId: string | null;
  firebaseUid: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  isLoading: boolean;
  isGuest: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  guestUserId: null,
  firebaseUid: null,
  login: async () => {},
  register: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
  resetPassword: async () => {},
  isLoading: true,
  isGuest: true,
});

export const useAuth = () => {
  return useContext(AuthContext);
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [firebaseUid, setFirebaseUidState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize guest user ID and Firebase UID
  useEffect(() => {
    const guestId = getOrCreateAnonUserId();
    setGuestUserId(guestId);
    
    // Load Firebase UID and email from localStorage if exists
    const savedFirebaseUid = getFirebaseUid();
    const savedFirebaseEmail = getFirebaseEmail();
    if (savedFirebaseUid) {
      setFirebaseUidState(savedFirebaseUid);
    }
  }, []);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      setIsLoading(false);
      
      if (user) {
        // Store Firebase UID and email in localStorage and state
        setFirebaseUid(user.uid);
        setFirebaseUidState(user.uid);
        if (user.email) {
          setFirebaseEmail(user.email);
        }
        // User is logged in, update guest user with Firebase UID
        await updateGuestUserWithFirebase(user.uid, user.email || '');
      } else {
        // User logged out, clear Firebase UID and email
        clearFirebaseUid();
        clearFirebaseEmail();
        setFirebaseUidState(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateGuestUserWithFirebase = async (firebaseUid: string, email: string) => {
    try {
      const guestId = getAnonUserId();
      if (!guestId) return;

      // For now, just log the guest user linking
      // TODO: Implement actual database operations when database is set up
    } catch (error) {
      console.error('Error linking guest user:', error);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      // Add additional scopes if needed
      provider.addScope('email');
      provider.addScope('profile');
      
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      clearFirebaseUid();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    guestUserId,
    firebaseUid,
    login,
    register,
    loginWithGoogle,
    logout,
    resetPassword,
    isLoading,
    isGuest: !user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};